// R2 세계 정수 틱 예약표. 취소된 경고는 즉시 반환하고 발동한 예약은 최소 간격 동안 남긴다.
import { HAZARD, REGION_STATUS } from '../shared/config';
import { Ev, HitFlag, TeleSource } from '../shared/events';
import { lavaAt } from '../shared/levels';
import { BossState, MobState, Scene, TeleShape } from '../shared/types';
import { telegraphHit } from './combat';
import type { GameSim } from './game';

export class ThreatClock {
  now = 0;
  private readonly times = new Float64Array(64).fill(-Infinity);
  private readonly owners = new Int16Array(64).fill(-1);
  reset(): void { this.now = 0; this.times.fill(-Infinity); this.owners.fill(-1); }
  clear(): void { this.times.fill(-Infinity); this.owners.fill(-1); }
  reserve(owner: number, at: number): boolean {
    let free = -1;
    for (let i = 0; i < this.times.length; i++) {
      if (Math.abs(this.times[i] - at) < HAZARD.minResolveGap) return false;
      if (this.times[i] <= this.now - HAZARD.minResolveGap) free = i;
    }
    if (free < 0) return false;
    this.times[free] = at; this.owners[free] = owner;
    return true;
  }
  cancel(owner: number): void {
    for (let i = 0; i < this.times.length; i++) if (this.owners[i] === owner && this.times[i] > this.now) {
      this.times[i] = -Infinity; this.owners[i] = -1;
    }
  }
}

export class Hazards {
  roomEnteredAt = 0;
  private readonly next = new Float64Array(32).fill(-1);
  private readonly slots = new Int16Array(32).fill(-1);
  private readonly resolves = new Float64Array(32);
  private lavaNext = -1;
  private poisonNext = -1;
  constructor(private readonly g: GameSim) {}
  reset(): void {
    for (let i = 0; i < this.slots.length; i++) this.g.releaseTelegraph(this.slots[i]);
    this.slots.fill(-1); this.next.fill(-1); this.lavaNext = -1; this.poisonNext = -1;
    this.roomEnteredAt = this.g.threats.now;
  }
  enterRoom(): void { this.reset(); }
  get ready(): boolean { return this.g.threats.now - this.roomEnteredAt >= HAZARD.roomGrace; }
  step(): void {
    const g = this.g, now = g.threats.now;
    if (g.flow.scene !== Scene.Dungeon) return;
    const vents = g.map.vents;
    if (!vents) return;
    for (let i = 0; i < vents.length; i++) {
      const v = vents[i], b = g.boss;
      const active = g.flow.roomState[v.room] === 1 && (v.fromBossHp === undefined ||
        (b.hp > 0 && b.state !== BossState.Dying && b.hp / b.maxHp <= v.fromBossHp));
      if (!active) { g.releaseTelegraph(this.slots[i]); this.slots[i] = -1; this.next[i] = -1; continue; }
      if (this.next[i] < 0) this.next[i] = (v.fromBossHp === undefined ? this.roomEnteredAt + HAZARD.roomGrace : Math.max(now, this.roomEnteredAt + HAZARD.roomGrace)) + Math.round(v.offset * HAZARD.ventPeriod);
      const slot = this.slots[i];
      if (slot >= 0) {
        const t = g.telegraphs[slot];
        t.tick = Math.min(t.total, t.total - (this.resolves[i] - now) + g.worldFrac);
        if (now < this.resolves[i]) continue;
        const hit = telegraphHit(t, g.hero.x, g.hero.z);
        g.fireTelegraph(slot); this.slots[i] = -1;
        const hurt = hit && g.combat.hurtHero(this.ventDamage, v.x, v.z, false, 2, HitFlag.Environment);
        if (hurt) this.afflict(g.map.hazardTheme);
        const e = g.emit(Ev.HazardErupt); e.x = v.x; e.z = v.z; e.a = i; e.b = v.radius; e.c = hurt ? 1 : 0;
        for (let m = 0; m < g.mobs.capacity; m++) {
          if (g.mobs.state[m] !== MobState.Inactive && g.mobs.state[m] !== MobState.Dying && Math.hypot(g.mobs.x[m] - v.x, g.mobs.z[m] - v.z) <= v.radius) g.combat.hazardMob(m, this.ventMobDamage);
        }
        if (g.flow.scene !== Scene.Dungeon) return;
      } else if (now >= this.next[i]) {
        const s = g.allocateTelegraph(TeleSource.Vent, i);
        if (s < 0) continue;
        if (!g.threats.reserve(s, now + HAZARD.ventWindup)) { g.releaseTelegraph(s); this.next[i] = now + 3; continue; }
        this.slots[i] = s; this.resolves[i] = now + HAZARD.ventWindup;
        this.next[i] = now + HAZARD.ventPeriod;
        const t = g.telegraphs[s];
        t.shape = TeleShape.Circle; t.x = v.x; t.z = v.z; t.radius = v.radius;
        t.angle = t.width = t.tick = 0; t.total = HAZARD.ventWindup;
        const w = g.emit(Ev.HazardWarn); w.x = v.x; w.z = v.z; w.a = i; w.b = v.radius; w.c = HAZARD.ventWindup;
      }
    }
  }
  /**
   * 분출 피해: R2 자유 입장은 기존 값 그대로, 스테이지 판·R3·R4는 그 판 위험 피해 배율(지역 × 스테이지, 하드 배율 없음)을 받는다.
   * 분출이 몹에게 주는 피해도 그 판 몹 HP 배율에 맞춰 R2 기준(2.3)과 같은 비율로 키운다
   */
  private get ventDamage(): number {
    const p = this.g.flowController.plan;
    return p.kind === 'free' && p.region === 'r2' ? HAZARD.ventDamage : Math.round(HAZARD.ventDamage * p.envDamageMult / 1.2);
  }
  private get ventMobDamage(): number {
    const p = this.g.flowController.plan;
    return p.kind === 'free' && p.region === 'r2' ? HAZARD.ventMobDamage : Math.round(HAZARD.ventMobDamage * p.mobHpMult / 2.3);
  }
  /**
   * 지역 테마 상태 이상 걸기(서리 = 둔화, 독 = 중독, C2 모래 = 발 묶임(둔화와 같은 규칙), 심연 = 침식(중독과 같은 규칙)). 정예 타격·분출·웅덩이가 부른다.
   * Ev.HeroStatus a: 1 서리 둔화 · 2 중독 · 4 모래 둔화 · 5 침식 시작(소리·표지가 테마별로 고른다)
   */
  afflict(theme: string | undefined): void {
    const h = this.g.hero;
    if (theme === 'frost' || theme === 'sand') {
      const fresh = h.chillTicks === 0;
      h.chillTicks = REGION_STATUS.chillTicks;
      if (fresh) { const e = this.g.emit(Ev.HeroStatus); e.x = h.x; e.z = h.z; e.a = theme === 'sand' ? 4 : 1; e.b = h.chillTicks; }
    } else if (theme === 'venom' || theme === 'void') {
      const fresh = h.poisonTicks === 0;
      h.poisonTicks = REGION_STATUS.poisonTicks;
      if (fresh) { const e = this.g.emit(Ev.HeroStatus); e.x = h.x; e.z = h.z; e.a = theme === 'void' ? 5 : 2; e.b = h.poisonTicks; this.poisonNext = this.g.heroTime + REGION_STATUS.poisonInterval; }
    }
  }
  /** 상태 이상 진행(영웅 시간, 마을·던전 모두 고정 스텝에서): 둔화·중독이 줄고, 중독은 간격마다 피해 */
  status(): void {
    const g = this.g, h = g.hero;
    if (g.flow.scene !== Scene.Dungeon) { h.chillTicks = 0; h.poisonTicks = 0; this.poisonNext = -1; return; }
    if (h.chillTicks > 0) h.chillTicks--;
    if (h.poisonTicks > 0) {
      h.poisonTicks--;
      // 방 입장(hazards.reset)으로 간격 시계가 지워졌어도 중독은 이어서 준다
      if (this.poisonNext < 0) this.poisonNext = g.heroTime + REGION_STATUS.poisonInterval;
      if (this.poisonNext >= 0 && g.heroTime >= this.poisonNext && h.hp > 0) {
        this.poisonNext = g.heroTime + REGION_STATUS.poisonInterval;
        if (!h.invulnerable) g.combat.burnHero(Math.max(1, Math.floor(h.maxHp * REGION_STATUS.poisonRatio)), 2);
      }
    } else this.poisonNext = -1;
  }
  // 화상은 영웅 시간에 따른다. 세계 슬로 중에도 영웅의 이동·회피와 같은 시간을 쓴다.
  // 웅덩이 테마(D3a): ember = 용암 화상, frost = 서리 균열(동상 + 둔화), venom = 독 늪(중독을 계속 새로 건다, 피해는 중독 틱이 준다).
  // C2: sand = 유사(서리 균열과 같은 규칙: 찰과상 + 둔화), void = 심연 균열(독 늪과 같은 규칙: 침식을 계속 새로 건다)
  burn(): void {
    const g = this.g, h = g.hero;
    if (g.flow.scene !== Scene.Dungeon || !lavaAt(g.grid, h.x, h.z)) { this.lavaNext = -1; return; }
    const theme = g.map.hazardTheme ?? 'ember';
    if (theme === 'venom' || theme === 'void') { if (!h.invulnerable) this.afflict(theme); return; }
    if (this.lavaNext >= 0 && g.heroTime < this.lavaNext) return;
    this.lavaNext = g.heroTime + HAZARD.lavaBurnInterval;
    if (h.invulnerable) return;
    if (theme === 'frost' || theme === 'sand') { this.afflict(theme); g.combat.burnHero(Math.floor(h.maxHp * REGION_STATUS.frostBurnRatio), 1); return; }
    g.combat.burnHero(Math.floor(h.maxHp * HAZARD.lavaBurnRatio));
  }
}
